import React, {FC, useCallback, useEffect, useRef, useState} from 'react'
import {toast, ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {KTSVG} from '../../../../_metronic/helpers'
import {useLocation, useNavigate} from 'react-router-dom'
import CreateReportModal from './CreateReportModal'
import {Company, CompanyAdmin, CreatedReports, Voyage} from '../core/_models'
import {getAssignmentById, getCompanyList, getConsumptionByVoyageReport, getCreatedReportById, getVoyageList} from '../core/_requests'
import DeleteCreatedReportModal from './DeleteCreatedReportModal'
import {getCompanyAdminList, getVesselList} from '../../Management/core/_requests'
import {Vessel} from '../../Management/core/_models'
import AddVoyageModal from './AddVoyageModal'
import isEqual from 'lodash/isEqual'
import {useAuth} from '../../auth'
import {OP} from '../core/operationPermissions'
import {useOperationsAccess} from '../core/useOperationsAccess'
import {useOperationsScopeUx} from '../core/operationsScopeUx'
import * as XLSX from 'xlsx'
import PositionReportNew from './PositionReportNew'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import {
  formatReportDateTimeDisplay,
  formatReportDateTimeFileToken,
} from '../core/reportDateTime'
import {resolveAssignmentTemplate, sortFieldsByOrder} from '../core/reportStructure'
import {
  renderConsumptionRobSectionHtml,
  renderGenericTabBodyHtml,
  renderMiscConsumablesTableHtml,
  renderNoFieldsBodyHtml,
  renderReportDocumentEndHtml,
  renderReportDocumentStartHtml,
  renderSectionHtml,
  ReportExportMeta,
} from '../core/reportExportHtml'
import { isFuelChangeoverReportName } from './fuelChangeoverReportSchema'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels, NO_ACCESSIBLE_VESSELS_MESSAGE } from '../../../shared/access/accessibleVessels'
import { TableSkeleton } from '../../../shared/components/LoadingSkeletons'
// ✅ ADD THIS near the top-level of the file (after imports) once per project
declare global {
  interface DocumentEventMap {
    'opslist-open-create-modal': CustomEvent<void>;
  }
}

const OperationsList: FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  // ---------------- NEW HELPERS START ----------------
  // label normalizer to match "Report Date Time" robustly
  const norm = (s: string) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim()

  // cache to avoid refetching same assignment repeatedly
  const assignmentCache = new Map<number, any>()

  // find the fieldId(s) inside an assignment template that correspond to "Report Date Time"
  const getReportDateFieldIds = (assignment: any): number[] => {
    const tmpl = assignment?.customTemplate ?? assignment?.template
    if (!tmpl?.menus) return []
    const ids: number[] = []
    for (const menu of tmpl.menus || []) {
      for (const sub of menu.submenus || []) {
        for (const f of sub.fields || []) {
          const label = norm(f.label || '')
          const isTarget =
            label === 'report date time' ||
            label.includes('report date') || // tolerant
            (f.fieldType === 'datetime' && label.includes('report'))
          if (isTarget) ids.push(f.id)
        }
      }
    }
    return ids
  }

  // read the reportDateTime from report.values by matching the field id(s)
  const pickReportDateFromValues = (report: any, reportDateFieldIds: number[]): string | null => {
    if (!Array.isArray(report?.values) || reportDateFieldIds.length === 0) return null
    // prefer exact label match if multiple
    for (const v of report.values) {
      if (reportDateFieldIds.includes(v.field)) {
        return v.valueText || null
      }
    }
    return null
  }

  // ISO date safe parser
  const toIsoOrNull = (s?: string | null) => {
    if (!s) return null
    const d = new Date(s)
    return isNaN(d.getTime()) ? null : d.toISOString()
  }

  const normalizeReportStatus = (status?: string | null) =>
    String(status ?? '')
      .trim()
      .toUpperCase()

  const getReportVoyageId = (report: any): number => {
    if (!report) return NaN
    if (typeof report.voyage === 'number') return report.voyage
    if (typeof report.voyage?.id === 'number') return report.voyage.id
    if (typeof report.voyageDto?.id === 'number') return report.voyageDto.id
    if (typeof report.voyageId === 'number') return report.voyageId
    const raw = report.voyage?.id ?? report.voyageDto?.id ?? report.voyageId ?? report.voyage
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : NaN
  }

  const getReportVesselId = (report: any): number => {
    if (!report) return NaN
    if (typeof report.vesselId === 'number') return report.vesselId
    if (typeof report.vessel?.id === 'number') return report.vessel.id
    if (typeof report.voyage?.vessel?.id === 'number') return report.voyage.vessel.id
    if (typeof report.voyageDto?.vessel?.id === 'number') return report.voyageDto.vessel.id
    if (typeof report.assignment?.vessel?.id === 'number') return report.assignment.vessel.id
    const raw =
      report.vesselId ??
      report.vessel?.id ??
      report.voyage?.vessel?.id ??
      report.voyageDto?.vessel?.id ??
      report.assignment?.vessel?.id ??
      report.vessel
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : NaN
  }

  const reportIdentityKey = (report: any, index: number) => {
    const reportId = Number(report?.id)
    if (Number.isFinite(reportId) && reportId > 0) return `id:${reportId}`
    const assignmentId = Number(report?.assignment?.id ?? report?.assignmentId ?? NaN)
    const voyageId = getReportVoyageId(report)
    const reportType = String(report?.reportType ?? report?.name ?? '').trim().toLowerCase()
    const reportDateTime = String(report?.reportDateTime ?? report?.submittedDateTime ?? '')
    return `fallback:${assignmentId || 'na'}:${voyageId || 'na'}:${reportType}:${reportDateTime}:${index}`
  }

  const dedupeReports = (items: any[]) => {
    const seen = new Map<string, any>()
    items.forEach((report, index) => {
      const key = reportIdentityKey(report, index)
      if (!seen.has(key)) {
        seen.set(key, report)
      }
    })
    return Array.from(seen.values())
  }
  // ---------------- NEW HELPERS END ----------------

  const {pathname} = useLocation()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false)
  const [isDeleteCreatedReportModalOpen, setIsDeleteCreatedReportModalOpen] =
    useState<boolean>(false)
  const closeModal = () => setIsCreateModalOpen(false)

  const [voyages, setVoyages] = useState<Voyage[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [vesselName, setVesselName] = useState('')
  const [vesselType, setVesselType] = useState('')
  const [imoNumber, setImoNumber] = useState('')
  const [selectedVoyage, setSelectedVoyage] = useState<Voyage | null>(null)
  const [selectedLegId, setSelectedLegId] = useState<number | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedVessel, setSelectedVessel] = useState<Vessel>()
  const { state: globalState } = useGlobalFilter()
  const [companies, setCompanies] = useState<Company[]>([])
  const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([])
  const [didUserSelectVoyage, setDidUserSelectVoyage] = useState(false)
  const [expandedCompanyAdminId, setExpandedCompanyAdminId] = useState<number | null>(null)
  const [expandedCompanyId, setExpandedCompanyId] = useState<number | null>(null)
  const [expandedVesselId, setExpandedVesselId] = useState<number | null>(null)
  const [expandedVoyageVesselIds, setExpandedVoyageVesselIds] = useState<Record<number, boolean>>({})

  const sortVoyages = useCallback((voyageList: Voyage[]): Voyage[] => {
    return [...voyageList].sort((a, b) => {
      const aCurrent = Boolean(a.active)
      const bCurrent = Boolean(b.active)
      if (aCurrent && !bCurrent) return -1
      if (!aCurrent && bCurrent) return 1

      const aTime = a.startDate ? new Date(a.startDate).getTime() : 0
      const bTime = b.startDate ? new Date(b.startDate).getTime() : 0
      return bTime - aTime
    })
  }, [])

  const renderVesselVoyageList = useCallback(
    (vesselId: number, rawVoyages: Voyage[]) => {
      const sorted = sortVoyages(rawVoyages)
      const isExpanded = Boolean(expandedVoyageVesselIds[vesselId])
      const visibleVoyages = isExpanded ? sorted : sorted.slice(0, 5)
      const hasMore = sorted.length > 5

      if (sorted.length === 0) {
        return (
          <div className='text-muted small p-2' style={{fontSize: '0.85rem'}}>
            No voyages available
          </div>
        )
      }

      return (
        <div>
          <ul className='list-group list-group-flush'>
            {visibleVoyages.map((voyage) => {
              const isActive = selectedVoyage?.id === voyage.id
              return (
                <li
                  key={voyage.id}
                  className={`list-group-item ${isActive ? 'current' : ''}`}
                  style={{cursor: 'pointer', border: 'none'}}
                  onClick={() => {
                    setSelectedVoyage(voyage)
                    setDidUserSelectVoyage(true)
                    if (vesselId > 0) setExpandedVesselId(vesselId)
                  }}
                >
                  <div
                    className='fw-bold'
                    title={`${voyage.voyageNumber}: ${voyage.departurePort} – ${voyage.arrivalPort}`}
                    style={{
                      color: '#111928',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'block',
                    }}
                  >
                    {voyage.voyageNumber}: {voyage.departurePort} – {voyage.arrivalPort}
                  </div>
                  <div className='text-muted'>
                    {new Date(voyage.startDate).toLocaleDateString('en-GB')} –{' '}
                    {voyage?.endDate
                      ? new Date(voyage.endDate).toLocaleDateString('en-GB')
                      : ''}
                  </div>
                  {voyage.active && (
                    <div className='badge bg-warning text-dark mt-1'>Current</div>
                  )}
                </li>
              )
            })}
          </ul>
          {hasMore && (
            <div className='text-center py-2 border-top'>
              <button
                type='button'
                className='btn btn-link btn-sm p-0 text-primary fw-semibold'
                style={{fontSize: '12px', textDecoration: 'none'}}
                onClick={(e) => {
                  e.stopPropagation()
                  setExpandedVoyageVesselIds((prev) => ({
                    ...prev,
                    [vesselId]: !isExpanded,
                  }))
                }}
              >
                {isExpanded ? 'View Less' : `+ View More (${sorted.length - 5} more)`}
              </button>
            </div>
          )}
        </div>
      )
    },
    [expandedVoyageVesselIds, selectedVoyage, sortVoyages]
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [draftReportId, setDraftReportId] = useState(0)
  const [reports, setReports] = useState<any[]>([])
  const rawReportsRef = useRef<any[]>([])
  const [loadingReports, setLoadingReports] = useState<boolean>(false)
  const [isAddVoyageModalOpen, setIsAddVoyageModalOpen] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<'view1' | 'view2'>('view1')
  // const roleId = sessionStorage.getItem("roleId");
  const {currentUser} = useAuth()
  const {opCan, isSuperAdmin} = useOperationsAccess()
  const scopeUx = useOperationsScopeUx(isSuperAdmin)

  const isCargoReport = (row: any) => {
    const typeName = row?.reportType || row?.assignment?.template?.name || ''
    return typeName.toLowerCase().includes('cargo')
  }

  const canReadReport = (row: any) => {
    if (isCargoReport(row)) return opCan(OP.READ_CARGO_OPERATION)
    return opCan(OP.READ_POSITION_REPORT)
  }

  const canUpdateReport = (row: any) => {
    const isSubmitted = normalizeReportStatus(row?.status) === 'SUBMITTED'
    if (isSubmitted && scopeUx.isCrew) {
      return false
    }
    if (isCargoReport(row)) return opCan(OP.UPDATE_CARGO_OPERATION)
    return opCan(OP.UPDATE_POSITION_REPORT)
  }

  const canDeleteReport = (row: any) => {
    if (isCargoReport(row)) return opCan(OP.DELETE_CARGO_OPERATION)
    return opCan(OP.DELETE_POSITION_REPORT)
  }

  const canDownloadReport = (row: any) => {
    if (isCargoReport(row)) return opCan(OP.DOWNLOAD_CARGO_OPERATION as any)
    return opCan(OP.DOWNLOAD_POSITION_REPORT as any)
  }

  const canCreateAnyReport = opCan(OP.CREATE_POSITION_REPORT)
  const canReadAnyReport = opCan(OP.READ_POSITION_REPORT) || opCan(OP.READ_CARGO_OPERATION)
  const canDownloadAnyReport = opCan(OP.DOWNLOAD_POSITION_REPORT as any) || opCan(OP.DOWNLOAD_COASTAL_POSITION_REPORT as any) || opCan(OP.DOWNLOAD_DAILY_PROGRESS_REPORT as any) || opCan(OP.DOWNLOAD_CARGO_OPERATION as any)
  const canEditSubmittedReports = isSuperAdmin || scopeUx.actsAsCompanyGroupAdmin
  const companyGroupAdminId = currentUser?.companyGroupAdminId
  const roleEntityId = currentUser?.roleEntityId
  const navigate = useNavigate()
  const [hasWarningsTrue, setHasWarningsTrue] = useState(0)
  const [hasWarningsFalse, setHasWarningsFalse] = useState(0)
  const [reportNameFilter, setReportNameFilter] = useState('')
  const [reportDateFilter, setReportDateFilter] = useState('')
  const [reportTimeFilter, setReportTimeFilter] = useState('')

  const filteredReports = reports.filter((report: any) => {
    const nameValue = (report?.reportType || '').toString().toLowerCase()
    const reportDateTime = report?.reportDateTime ? new Date(report.reportDateTime) : null

    const reportIsoDate =
      reportDateTime && !Number.isNaN(reportDateTime.getTime())
        ? reportDateTime.toISOString().slice(0, 10)
        : ''

    const reportHhMm =
      reportDateTime && !Number.isNaN(reportDateTime.getTime())
        ? `${String(reportDateTime.getHours()).padStart(2, '0')}:${String(
            reportDateTime.getMinutes()
          ).padStart(2, '0')}`
        : ''

    const matchReportName =
      !reportNameFilter || nameValue.includes(reportNameFilter.trim().toLowerCase())
    const matchReportDate = !reportDateFilter || reportIsoDate === reportDateFilter
    const matchReportTime = !reportTimeFilter || reportHhMm === reportTimeFilter
    const reportLegId = report?.legId ?? report?.legDto?.id ?? report?.leg?.id
    const matchLeg = selectedLegId == null || (reportLegId != null && Number(reportLegId) === selectedLegId)

    return matchReportName && matchReportDate && matchReportTime && matchLeg
  })

  const indexOfLast = currentPage * rowsPerPage
  const indexOfFirst = indexOfLast - rowsPerPage
  const currentReports = filteredReports.slice(indexOfFirst, indexOfLast)
  // const roleEntityId = sessionStorage.getItem("roleEntityId");
  const totalPages = Math.max(1, Math.ceil(filteredReports.length / rowsPerPage))
  const [sortConfig, setSortConfig] = useState<{
    key: 'reportType' | 'voyageNumber' | 'reportDateTime' | 'createdBy' | 'status' | null
    direction: 'asc' | 'desc'
  }>({
    key: 'reportDateTime', // default sort by Report Date & Time
    direction: 'desc', // latest first
  })

  const openCreateModal = useCallback(async () => {
    if (!selectedVoyage) {
      toast.warn('Please select a voyage first to create a report.', {position: 'top-center'})
      return
    }

    const voyageId = Number(selectedVoyage.id)
    const vesselId = Number(
      selectedVoyage?.vessel?.id ?? selectedVessel?.id ?? currentUser?.vessel?.id ?? 0
    )

    // Check if an active draft exists for this voyage
    let activeDraft: any = null

    // 1. Check in rawReportsRef if available
    if (rawReportsRef.current && rawReportsRef.current.length > 0) {
      activeDraft = rawReportsRef.current.find((r: any) => {
        if (normalizeReportStatus(r.status) !== 'DRAFT') return false
        const rVoyId = getReportVoyageId(r)
        return Number.isFinite(rVoyId) && rVoyId === voyageId
      })
    }

    // 2. Also check in reports state
    if (!activeDraft && reports && reports.length > 0) {
      activeDraft = reports.find((r: any) => {
        if (normalizeReportStatus(r.status) !== 'DRAFT') return false
        const rVoyId = getReportVoyageId(r)
        return Number.isFinite(rVoyId) && rVoyId === voyageId
      })
    }

    // 3. Fallback: fetch fresh from server to guarantee accuracy
    if (!activeDraft) {
      try {
        const freshReports = await getCreatedReportById(vesselId || undefined)
        if (Array.isArray(freshReports)) {
          rawReportsRef.current = freshReports
          activeDraft = freshReports.find((r: any) => {
            if (normalizeReportStatus(r.status) !== 'DRAFT') return false
            const rVoyId = getReportVoyageId(r)
            return Number.isFinite(rVoyId) && rVoyId === voyageId
          })
        }
      } catch (err) {
        console.warn('Failed to verify active draft on voyage:', err)
      }
    }

    if (activeDraft) {
      toast.error(
        `An active draft report already exists for this voyage (${activeDraft.reportType || 'Draft Report'}). You cannot create a new report until the existing draft is submitted or deleted.`,
        {position: 'top-center'}
      )
      return
    }

    setIsCreateModalOpen(true)
  }, [reports, selectedVoyage, selectedVessel, currentUser])

  useEffect(() => {
    const handler = () => {
      void openCreateModal()
    }
    // TS-safe: our declaration above lets the event name type-check
    document.addEventListener('opslist-open-create-modal', handler as EventListener)

    return () => {
      document.removeEventListener('opslist-open-create-modal', handler as EventListener)
    }
  }, [openCreateModal])

  const handleSort = (key: typeof sortConfig.key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }
  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value))
    setCurrentPage(1) // reset to first page
  }

  const clearReportFilters = () => {
    setReportNameFilter('')
    setReportDateFilter('')
    setReportTimeFilter('')
    setCurrentPage(1)
  }

  useEffect(() => {
    if (selectedVoyage !== null) {
      setSearchTerm('')
    }
  }, [selectedVoyage])

  useEffect(() => {
    fetchVoyages()
    if (scopeUx.actsAsSuperadmin) {
      fetchCompanyAdmins()
    }
    fetchCompanies()
    fetchVessels()
  }, [])

  useEffect(() => {
    if (selectedVessel && !vessels.some((vessel) => vessel.id === selectedVessel.id)) {
      setSelectedVessel(undefined)
    }
  }, [selectedVessel, vessels])

  useEffect(() => {
    setDidUserSelectVoyage(false)
  }, [pathname])

  useEffect(() => {
    console.log('current reports', currentReports)
  }, [currentReports])

  useEffect(() => {
    console.log('role entity id', roleEntityId)
  }, [roleEntityId])

  useEffect(() => {
    if (!voyages.length) return

    // Expand the first vessel/accordion so the user can easily choose a voyage, but do not auto-select default reports
    const firstVoyage = voyages[0]
    if (firstVoyage) {
      if (firstVoyage.companyGroupAdminId) {
        setExpandedCompanyAdminId(firstVoyage.companyGroupAdminId)
      }
      if (firstVoyage.companyAdminId) {
        setExpandedCompanyId(firstVoyage.companyAdminId)
      }
      if (firstVoyage.vessel?.id) {
        setExpandedVesselId(firstVoyage.vessel.id)
      }
    }
  }, [voyages])

  // Sync global header vessel filter → selectedVessel
  useEffect(() => {
    const gv = globalState.vessel
    if (gv) {
      setSelectedVessel(gv)
    } else {
      setSelectedVessel(undefined)
    }
  }, [globalState.vessel])

  // Sync global header company filter → expand accordion
  useEffect(() => {
    const cg = globalState.company
    if (cg) {
      if (cg.type === 'company_group') {
        setExpandedCompanyAdminId(cg.id)
        setExpandedCompanyId(null)
      } else {
        // company_admin: expand both company group and sub-company
        const vessel = vessels.find((v: any) =>
          Number(v.companyAdminId ?? v.companyAdmin?.id ?? v.companyId) === cg.id
        )
        const v = vessel as any
        const parentCgId = v
          ? Number(v.companyGroupAdminId ?? v.companyGroupAdmin?.id ?? v.companyGroupId)
          : null
        if (Number.isFinite(parentCgId)) {
          setExpandedCompanyAdminId(parentCgId!)
        }
        setExpandedCompanyId(cg.id)
      }
      setSelectedVessel(undefined)
    }
  }, [globalState.company, vessels])

  // Sync global header search query → searchTerm
  useEffect(() => {
    setSearchTerm(globalState.searchQuery || '')
  }, [globalState.searchQuery])
  useEffect(() => {
    if (selectedVoyage) {
      setImoNumber(selectedVoyage?.vessel?.imoNumber || '')
      setVesselType(selectedVoyage?.vessel?.vesselType || '')
      setVesselName(selectedVoyage?.vessel?.fleet_name || '')
      setSelectedLegId(null)
      fetchCreatedReportsList()
    } else {
      setImoNumber('')
      setVesselType('')
      setVesselName('')
      setSelectedLegId(null)
      setReports([])
      setLoadingReports(false)
    }
  }, [selectedVoyage])

  useEffect(() => {
    if (!reports || reports.length === 0) return

    const {hasWarningsTrue, hasWarningsFalse} = reports.reduce(
      (acc, report) => {
        if (report.hasWarnings) {
          acc.hasWarningsTrue += 1
        } else {
          acc.hasWarningsFalse += 1
        }
        return acc
      },
      {hasWarningsTrue: 0, hasWarningsFalse: 0}
    )

    setHasWarningsTrue(hasWarningsTrue)
    setHasWarningsFalse(hasWarningsFalse)
  }, [reports])

  useEffect(() => {
    if (searchTerm.trim() !== '' || selectedVoyage) {
      fetchCreatedReportsList()
    }
  }, [searchTerm])
  useEffect(() => {
    console.log(voyages)
  }, [voyages])

  useEffect(() => {
    if (!sortConfig.key) return

    const key = sortConfig.key
    const dir = sortConfig.direction === 'asc' ? 1 : -1

    const sorted = [...reports].sort((a: any, b: any) => {
      let valA: any
      let valB: any

      if (key === 'createdBy') {
        valA = a.createdBy?.rank || ''
        valB = b.createdBy?.rank || ''
        valA = valA.toString().toLowerCase()
        valB = valB.toString().toLowerCase()
      } else if (key === 'reportDateTime') {
        const da = a.reportDateTime ? new Date(a.reportDateTime).getTime() : 0
        const db = b.reportDateTime ? new Date(b.reportDateTime).getTime() : 0
        return (da - db) * dir
      } else {
        valA = a[key] ?? ''
        valB = b[key] ?? ''
        if (typeof valA === 'string') valA = valA.toLowerCase()
        if (typeof valB === 'string') valB = valB.toLowerCase()
      }

      if (valA < valB) return -1 * dir
      if (valA > valB) return 1 * dir
      return 0
    })

    setReports(sorted)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortConfig.key, sortConfig.direction]) // avoid depending on reports.length to prevent loops

  const handleSearchChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const vesselName = e.target.value
    setSearchTerm(vesselName)

    // Only search for vessels if vesselName is not empty
    if (vesselName.trim() !== '') {
      const searchedVessel = vessels.filter((vessel: any) =>
        vessel.fleet_name.toLowerCase().includes(vesselName.toLowerCase())
      )

      // Only set the selected vessel if there's at least one match
      if (searchedVessel.length > 0) {
        setSelectedVessel(searchedVessel[0]) // Set the first matching vessel
      } else {
        setSelectedVessel(undefined) // Reset if no matches found
      }

      setVesselName(searchedVessel[0].fleet_name)
      setImoNumber(searchedVessel[0].imoNumber)
      setVesselType(searchedVessel[0].vesselType)
      setSelectedVoyage(null)

      console.log(searchedVessel) // Log the filtered vessels
    } else {
      // If the search term is empty, reset the selected vessel and voyage
      setSelectedVessel(undefined)
      setSelectedVoyage(null)
    }
  }

  const filteredVessels = vessels.filter((vessel) =>
    vessel.fleet_name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const fetchCompanyAdmins = async () => {
    try {
      const companyList = await getCompanyAdminList()
      console.log('Company List:', companyList)

      const activeCompanies = companyList.filter((company) => company.active === true)

      setCompanyAdmins(activeCompanies)
    } catch (error) {
      console.error('Failed to fetch company list:', error)
    }
  }

  const mapCreatedValuesToFormData = (
    values: CreatedReports['values'],
    menus:
      | CreatedReports['assignment']['template']['menus']
      | CreatedReports['assignment']['customTemplate']['menus']
  ) => {
    const result: {
      [submenuId: number]: {
        [id: number]: {
          fieldLabel: string
          value: any
        }
      }
    } = {}

    if (!values || !Array.isArray(values) || !menus || !Array.isArray(menus)) return result

    for (const entry of values) {
      const rawField = (entry as any)?.field
      const fieldId =
        typeof rawField === 'object' && rawField !== null
          ? Number(rawField.id)
          : Number(rawField)
      if (Number.isNaN(fieldId) || fieldId === undefined || fieldId === null) continue

      const fieldValue =
        entry.valueText !== undefined && entry.valueText !== null
          ? String(entry.valueText)
          : (entry as any).value !== undefined && (entry as any).value !== null
          ? String((entry as any).value)
          : ''

      for (const menu of menus) {
        if (!menu?.submenus) continue
        for (const submenu of menu.submenus) {
          const matchingField = submenu.fields?.find((field) => Number(field.id) === fieldId)

          if (matchingField) {
            // Checkbox/multi-select fields store each selected option as a
            // separate value entry sharing the same field id. Aggregate them
            // into an array so the preview renders every selected option
            // (mirrors the edit-mode mapping in CreateReport).
            const isCheckbox = (matchingField as any).fieldType === 'checkbox'

            if (!result[submenu.id]) {
              result[submenu.id] = {}
            }

            if (!result[submenu.id][matchingField.id]) {
              result[submenu.id][matchingField.id] = {
                fieldLabel: matchingField.label,
                value: isCheckbox ? [fieldValue] : fieldValue,
              }
            } else if (isCheckbox) {
              if (Array.isArray(result[submenu.id][matchingField.id].value)) {
                ;(result[submenu.id][matchingField.id].value as any[]).push(fieldValue)
              } else {
                result[submenu.id][matchingField.id].value = [
                  result[submenu.id][matchingField.id].value,
                  fieldValue,
                ]
              }
            }
          }
        }
      }
    }

    return result
  }

  const handlePreviewReportView = async (draftReport: CreatedReports) => {
    try {
      if (!draftReport?.assignment?.id) return

      const assignment = await getAssignmentById(draftReport.assignment.id)
      const templateData = resolveAssignmentTemplate(assignment)

      if (!templateData || !templateData.menus) {
        toast.error('No valid report template found.')
        return
      }

      const flattenedSubmenus = templateData.menus.flatMap((menu: any) => menu.submenus || [])
      const activeSubmenus = templateData.menus
        .filter((menu: any) => menu.isActive)
        .flatMap((menu: any) => menu.submenus.filter((submenu: any) => submenu.isActive))
      const reportDateTime =
        pickReportDateFromValues(draftReport, getReportDateFieldIds(assignment)) ||
        draftReport.reportDateTime ||
        draftReport.submittedDateTime ||
        draftReport.createdDateTime ||
        new Date().toISOString()

      const mappedFormData = mapCreatedValuesToFormData(draftReport.values, templateData.menus)
      const reportIdFromValues = Array.isArray(draftReport?.values)
        ? Number(
            draftReport.values.find(
              (v: any) => Number(v?.reportId) > 0 || Number(v?.report) > 0
            )?.reportId ||
              draftReport.values.find(
                (v: any) => Number(v?.reportId) > 0 || Number(v?.report) > 0
              )?.report ||
              0
          )
        : 0

      console.log(draftReport)
      navigate('/operations/report-preview', {
        state: {
          formData: mappedFormData,
          tabs: activeSubmenus,
          assignmentId: assignment.id,
          selectedVoyage: selectedVoyage,
          selectedReport: draftReport,
          reportDate: reportDateTime,
          stepCompletionStatus: [],
          readOnly: true,
          allValues: draftReport.values,
          reportId: reportIdFromValues || draftReport.id,
          vesselId: selectedVoyage?.vessel?.id, // pass explicitly
        },
      })
    } catch (err) {
      console.error('Error while preparing preview:', err)
      toast.error('Failed to open preview.')
    }
  }

  const handleDownload = async (draftReport: CreatedReports) => {
    try {
      if (!draftReport?.assignment?.id) return

      // ✅ Fetch assignment & template
      const assignment = await getAssignmentById(draftReport.assignment.id)
      const templateData = resolveAssignmentTemplate(assignment)

      if (!templateData || !templateData.menus) {
        toast.error('No valid report template found.')
        return
      }

      // ✅ Filter active submenus
      const activeSubmenus = templateData.menus
        .filter((menu: any) => menu.isActive)
        .flatMap((menu: any) => menu.submenus.filter((submenu: any) => submenu.isActive))

      // ✅ Map values into form data
      const mappedFormData = mapCreatedValuesToFormData(draftReport.values, templateData.menus)
      const reportDateTime =
        pickReportDateFromValues(draftReport, getReportDateFieldIds(assignment)) ||
        draftReport.reportDateTime ||
        draftReport.submittedDateTime ||
        draftReport.createdDateTime ||
        new Date().toISOString()
      const reportDateTimeText =
        reportDateTime instanceof Date ? reportDateTime.toISOString() : reportDateTime || ''

      const pdfContainer = document.getElementById('pdf-preview')
      if (!pdfContainer) return

      // ✅ Fetch consumption data so the download mirrors the on-screen view
      let consumptionData: any = null
      const hasConsumptionTab = activeSubmenus.some((tab: any) => tab.name === 'Consumption & ROB')
      if (hasConsumptionTab) {
        try {
          const voyageId = getReportVoyageId(draftReport)
          if (Number.isFinite(voyageId) && voyageId > 0 && Number(draftReport?.id) > 0) {
            consumptionData = await getConsumptionByVoyageReport(voyageId, Number(draftReport.id))
          }
        } catch (err) {
          console.error('Failed to fetch consumption for download:', err)
        }
      }

      pdfContainer.innerHTML = ''
      const content = document.createElement('div')

      // Header section — enterprise document chrome shared with PreviewReportModal.
      const isBunkerExport = /\bbunker\b/i.test(String(draftReport.reportType ?? ''))
      const isFuelChangeoverExport = isFuelChangeoverReportName(
        String(draftReport.reportType ?? '')
      )

      const exportMeta: ReportExportMeta = {
        title: draftReport.reportType,
        vesselName: selectedVoyage?.vessel?.fleet_name,
        imoNumber: selectedVoyage?.vessel?.imoNumber,
        vesselType: selectedVoyage?.vessel?.vesselType,
        voyageNumber: selectedVoyage?.voyageNumber,
        departurePort: selectedVoyage?.departurePort,
        arrivalPort: selectedVoyage?.arrivalPort,
        reportDate: formatReportDateTimeDisplay(reportDateTimeText) || '--',
      }

      let html = renderReportDocumentStartHtml(exportMeta)

      // ✅ One styled section per tab, mirroring the on-screen view.
      activeSubmenus.forEach((tab: any) => {
        const fieldValues = mappedFormData[tab.id] || {}
        const activeFields = ((tab.fields || []) as any[]).filter((field: any) => field.isActive)

        if (activeFields.length === 0) {
          html += renderSectionHtml(tab.name, renderNoFieldsBodyHtml())
          return
        }

        if (tab.name === 'Consumption & ROB') {
          html += renderSectionHtml(
            tab.name,
            renderConsumptionRobSectionHtml(consumptionData, {
              isFuelChangeoverReport: isFuelChangeoverExport,
            })
          )
          return
        }

        if (tab.name === 'Miscellaneous Consumables') {
          const miscMerged = sortFieldsByOrder(activeFields).map((field: any) => {
            const value = fieldValues?.[field.id]?.value ?? '--'
            return [field.label, value] as [string, any]
          })
          html += renderSectionHtml(tab.name, renderMiscConsumablesTableHtml(miscMerged))
          return
        }

        html += renderSectionHtml(
          tab.name,
          renderGenericTabBodyHtml(tab.fields || [], fieldValues, {isBunkerReport: isBunkerExport})
        )
      })

      html += renderReportDocumentEndHtml(exportMeta)

      content.innerHTML = html
      pdfContainer.appendChild(content)

      // ✅ Generate the PDF
      setTimeout(() => {
        import('html2pdf.js').then((html2pdf) => {
          html2pdf
            .default()
            .from(pdfContainer)
            .set({
              margin: 0.5,
              filename: `${draftReport.reportType || ''}_${
                formatReportDateTimeFileToken(reportDateTimeText) || 'report'
              }.pdf`,
              html2canvas: {scale: 2},
              pagebreak: {mode: ['css', 'legacy']},
              jsPDF: {unit: 'in', format: 'a4', orientation: 'portrait'},
            })
            .save()
        })
      }, 300)
    } catch (err) {
      console.error('Error while preparing preview:', err)
      toast.error('Failed to open preview.')
    }
  }

  const fetchCompanies = async () => {
    try {
      const companyList = await getCompanyList()
      console.log(companyList)

      setCompanies(companyList)
    } catch (error) {
      console.error('Failed to fetch companies:', error)
    }
  }

  const fetchVessels = async () => {
    try {
      const vesselList = normalizeAccessibleVessels(await fetchAccessibleVesselsForCurrentUser())
      console.log('Vessel List:', vesselList)

      setVessels(vesselList)
    } catch (error) {
      console.error('Failed to fetch company list:', error)
    }
  }

  const fetchVoyages = async () => {
    try {
      const voyageList = await getVoyageList()

      setVoyages(voyageList)
    } catch (error) {
      console.error('Failed to fetch voyage list:', error)
    }
  }

  const fetchCreatedReportsList = async () => {
    const currentVoyageId = selectedVoyage?.id != null ? Number(selectedVoyage.id) : null
    const currentVesselId =
      selectedVessel?.id != null
        ? Number(selectedVessel.id)
        : selectedVoyage?.vessel?.id != null
        ? Number(selectedVoyage.vessel.id)
        : undefined
    const searchNeedle = searchTerm.trim().toLowerCase()

    // If no voyage is selected and no search needle entered, do not show default reports
    if (!currentVoyageId && !searchNeedle) {
      setReports([])
      setLoadingReports(false)
      return
    }

    try {
      setLoadingReports(true)
      // 1) Fetch reports from server (vessel-scoped if vesselId is known)
      const reportsList = await getCreatedReportById(currentVesselId)
      rawReportsRef.current = Array.isArray(reportsList) ? reportsList : []

      // 2) Filter by selected voyage or search needle
      const searchNeedle = searchTerm.trim().toLowerCase()
      let filteredReports = Array.isArray(reportsList) ? reportsList : []

      if (searchNeedle) {
        filteredReports = filteredReports.filter((report: any) => {
          const reportVesselId = getReportVesselId(report)
          const vesselName = String(
            report?.vessel?.fleet_name ??
              report?.vesselName ??
              report?.vessel_name ??
              report?.voyageDto?.vessel?.fleet_name ??
              ''
          ).toLowerCase()
          return (
            (currentVesselId != null && reportVesselId === currentVesselId) ||
            vesselName.includes(searchNeedle) ||
            String(report?.reportType ?? report?.name ?? '').toLowerCase().includes(searchNeedle)
          )
        })
      } else if (currentVoyageId != null && Number.isFinite(currentVoyageId)) {
        filteredReports = filteredReports.filter((report: any) => {
          const reportVoyageId = getReportVoyageId(report)
          return Number.isFinite(reportVoyageId) && reportVoyageId === currentVoyageId
        })
      }

      // 3) Remove drafts for certain roles
      if (isSuperAdmin || scopeUx.isCompanyAdmin || scopeUx.isCompanyGroupAdmin) {
        filteredReports = filteredReports.filter(
          (report: any) => normalizeReportStatus(report.status) !== 'DRAFT'
        )
      }

      filteredReports = dedupeReports(filteredReports)

      // 4) Instant in-memory date enrichment & voyage/leg mapping (no N+1 network requests!)
      const voyageMap = new Map(voyages.map((v) => [Number(v.id), v]))
      const enrichedWithVoyageNumber = filteredReports.map((report: any) => {
        const reportDateTime =
          toIsoOrNull(report?.reportDateTime) ||
          toIsoOrNull(report?.submittedDateTime) ||
          toIsoOrNull(report?.createdDateTime) ||
          null

        const reportVoyageId = getReportVoyageId(report)
        const relatedVoyage = voyageMap.get(reportVoyageId)
        const voyageNumber =
          report?.voyageNumber ??
          report?.voyageDto?.voyageNumber ??
          relatedVoyage?.voyageNumber ??
          (reportVoyageId === Number(selectedVoyage?.id) ? selectedVoyage?.voyageNumber : null)

        // Resolve leg info from report, or lookup from voyage legs by legId
        const reportVoyage =
          relatedVoyage || (reportVoyageId === Number(selectedVoyage?.id) ? selectedVoyage : null)
        let legId = report?.legId ?? report?.legDto?.id ?? report?.leg?.id ?? null
        let legDto = report?.legDto ?? report?.leg ?? null
        let legSequenceNumber =
          report?.legSequenceNumber ?? report?.legDto?.sequenceNumber ?? report?.leg?.sequenceNumber ?? null

        if (legId && !legDto && reportVoyage?.legs) {
          const matchedLeg = reportVoyage.legs.find((l: any) => Number(l.id) === Number(legId))
          if (matchedLeg) {
            legDto = matchedLeg
            legSequenceNumber = matchedLeg.sequenceNumber
          }
        } else if (!legId && reportVoyage?.legs && reportVoyage.legs.length > 0) {
          const fallbackLeg =
            reportVoyage.legs.find((l: any) => l.active) || reportVoyage.legs[0]
          if (fallbackLeg) {
            legId = fallbackLeg.id
            legDto = fallbackLeg
            legSequenceNumber = fallbackLeg.sequenceNumber
          }
        }

        return {
          ...report,
          reportDateTime,
          voyageNumber: voyageNumber || '--',
          legId,
          legDto,
          legSequenceNumber,
        }
      })

      // 5) Default sort: latest first on reportDateTime
      enrichedWithVoyageNumber.sort((a, b) => {
        const da = a.reportDateTime ? new Date(a.reportDateTime).getTime() : 0
        const db = b.reportDateTime ? new Date(b.reportDateTime).getTime() : 0
        return db - da
      })

      // 6) Only update state if the array really changed
      setReports((prev) => (isEqual(prev, enrichedWithVoyageNumber) ? prev : enrichedWithVoyageNumber))
    } catch (error) {
      console.error('Failed to fetch created reports list:', error)
    } finally {
      setLoadingReports(false)
    }
  }

  const handleExcelDownload = () => {
    const table = document.querySelector('.report-table table') as HTMLTableElement
    if (!table) return

    const workbook = XLSX.utils.table_to_book(table, {sheet: 'reports'})
    XLSX.writeFile(workbook, 'reports.xlsx')
  }

  return (
    <div
      className='app-main flex-column flex-row-fluid'
      id='kt_app_main'
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        height: embedded ? '100%' : undefined,
        maxHeight: embedded ? '100%' : undefined,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <div
        className='d-flex flex-column flex-column-fluid'
        style={{display: 'flex', flexDirection: 'column', flex: 1, height: embedded ? '100%' : undefined, maxHeight: embedded ? '100%' : undefined, minHeight: 0, overflow: 'hidden'}}
      >
        <div
          id='kt_app_content'
          className='app-content flex-column-fluid d-flex flex-column '
          style={{display: 'flex', flexDirection: 'column', flex: 1, height: embedded ? '100%' : undefined, maxHeight: embedded ? '100%' : undefined, minHeight: 0, overflow: 'hidden'}}
        >
          <div
            className='card flex-column-fluid d-flex flex-column'
            style={{display: 'flex', flexDirection: 'column', flex: 1, height: embedded ? '100%' : undefined, maxHeight: embedded ? '100%' : undefined, minHeight: 0, overflow: 'hidden', borderRadius: 0}}
          >
            {/* Header */}
            <div className='d-flex border-top' style={{flex: 1, minHeight: 0, overflow: 'hidden'}}>
              <div
                className={`border-end pt-3 ps-1 overflow-auto`}
                style={{
                  height: '100%',
                  maxHeight: '100%',
                  overflowY: 'auto',
                  backgroundColor: '#ffffff',
                  maxWidth: '18rem',
                }}
              >
                <div className='px-5 mb-4'>
                  <div className='d-flex justify-content-between align-items-center'>
                    {scopeUx.isCrew && (
                      <>
                        <h6 className='mb-3'>Voyage List</h6>
                        <p style={{color: '#6B7280', fontWeight: '400'}}>
                          {voyages.length} Voyages
                        </p>
                      </>
                    )}
                    {scopeUx.actsAsSuperadmin && (
                        <>
                          <h6 className='mb-3'>Vessels</h6>
                          <p style={{color: '#6B7280', fontWeight: '400'}}>
                            {vessels.filter((v) => {
                              if (!v.active) return false
                              if (globalState.company) {
                                if (globalState.company.type === 'company_group') {
                                  return v.companyGroupAdmin?.id === globalState.company.id
                                }
                                if (globalState.company.type === 'company_admin') {
                                  return v.companyAdmin?.id === globalState.company.id
                                }
                              }
                              return true
                            }).length} vessels
                          </p>
                        </>
                      )}
                  </div>
                  <div className='d-flex align-items-center gap-2'>
                    <input
                      type='text'
                      className='form-control form-control-sm'
                      placeholder='Search vessel or voyage...'
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                {/* <div className='d-flex justify-content-end py-4 pe-5 gap-1'>
                                    <KTSVG path='/media/map/complete.svg' className='svg-icon-2' /><span className='text-muted pe-1'>{hasWarningsTrue}</span>
                                    <KTSVG path='/media/map/Incomplete.svg' className='svg-icon-2' /><span className='text-muted'>{hasWarningsFalse}</span>
                                </div> */}
                {/* <h5 className="mt-6 mb-3">Vessel Name</h5> */}
                {/* Crew member */}
                {selectedVessel && (
                  <div className='accordion' id='vesselAccordionRole2'>
                    <div key={selectedVessel.id} className='accordion-item'>
                      <h2 className='accordion-header' id={`heading-vessel-${selectedVessel.id}`}>
                        <button
                          className='accordion-button'
                          type='button'
                          data-bs-toggle='collapse'
                          data-bs-target={`#collapse-vessel-${selectedVessel.id}`}
                          aria-expanded={true}
                          aria-controls={`collapse-vessel-${selectedVessel.id}`}
                        >
                          <VesselLandingLink
                            vesselId={selectedVessel.id}
                            label={selectedVessel.fleet_name}
                            className='text-dark text-decoration-underline'
                          />
                        </button>
                      </h2>

                      <div
                        id={`collapse-vessel-${selectedVessel.id}`}
                        className='accordion-collapse show' // Ensures the accordion is open
                        aria-labelledby={`heading-vessel-${selectedVessel.id}`}
                        data-bs-parent='#vesselAccordionRole2'
                      >
                        <div className='accordion-body'>
                          {/* Display voyages for the selected vessel */}
                          {renderVesselVoyageList(
                            selectedVessel.id,
                            voyages.filter((v) => v.vessel?.id === selectedVessel.id)
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {scopeUx.isCrew && !selectedVessel && renderVesselVoyageList(0, voyages)}
                {/* Superadmin */}
                {scopeUx.actsAsSuperadmin &&
                  !selectedVessel && (
                    <div className='accordion' id='companyAdminAccordion'>
                      {companyAdmins
                        .filter((admin) => {
                          if (!globalState.company) return true
                          if (globalState.company.type === 'company_group') {
                            return admin.id === globalState.company.id
                          }
                          if (globalState.company.type === 'company_admin') {
                            const adminComps = companies.filter(
                              (c) => c.cga?.id === admin.id && c.active
                            )
                            return adminComps.some((c) => c.id === globalState.company!.id)
                          }
                          return true
                        })
                        .map((admin) => {
                          const isAdminOpen = expandedCompanyAdminId === admin.id
                          const adminVessels = vessels.filter(
                            (v) => v.companyGroupAdmin?.id === admin.id && v.active
                          )
                          const orphanVessels = adminVessels
                            .filter((v) => !v.companyAdmin?.id)
                            .filter(() => globalState.company?.type !== 'company_admin')
                          const adminCompanies = companies
                            .filter((c) => c.cga?.id === admin.id && c.active)
                            .filter((c) => {
                              if (!globalState.company) return true
                              if (globalState.company.type === 'company_admin') {
                                return c.id === globalState.company.id
                              }
                              return true
                            })

                          return (
                            <div key={admin.id} className='accordion-item'>
                              <h2 className='accordion-header' id={`heading-admin-${admin.id}`}>
                                <button
                                  className={`accordion-button ${isAdminOpen ? '' : 'collapsed'}`}
                                  type='button'
                                  data-bs-toggle='collapse'
                                  data-bs-target={`#collapse-admin-${admin.id}`}
                                  aria-expanded={isAdminOpen}
                                  aria-controls={`collapse-admin-${admin.id}`}
                                  onClick={() =>
                                    setExpandedCompanyAdminId(isAdminOpen ? null : admin.id)
                                  }
                                >
                                  <div style={{maxWidth: '180px'}}>
                                    <span
                                      className='fw-semibold small text-truncate'
                                      title={admin.name}
                                      style={{
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'inline-block',
                                      }}
                                    >
                                      {admin.name}
                                    </span>
                                  </div>
                                </button>
                              </h2>
                              <div
                                id={`collapse-admin-${admin.id}`}
                                className={`accordion-collapse collapse ${isAdminOpen ? 'show' : ''}`}
                                aria-labelledby={`heading-admin-${admin.id}`}
                                data-bs-parent='#companyAdminAccordion'
                              >
                                <div className='accordion-body py-3'>
                                  {/* 1) Orphan vessels */}
                                  {orphanVessels.map((vessel) => {
                                    const isOrphanOpen = expandedVesselId === vessel.id
                                    const vesselVoyages = voyages.filter(
                                      (vo) => vo.vessel?.id === vessel.id
                                    )

                                    return (
                                      <div key={vessel.id} className='accordion mb-3'>
                                        <div className='accordion-item'>
                                          <h2
                                            className='accordion-header'
                                            id={`heading-orphan-${vessel.id}`}
                                          >
                                            <button
                                              className={`accordion-button ${
                                                isOrphanOpen ? '' : 'collapsed'
                                              }`}
                                              type='button'
                                              data-bs-toggle='collapse'
                                              data-bs-target={`#collapse-orphan-${vessel.id}`}
                                              aria-expanded={isOrphanOpen}
                                              aria-controls={`collapse-orphan-${vessel.id}`}
                                              onClick={() =>
                                                setExpandedVesselId(isOrphanOpen ? null : vessel.id)
                                              }
                                              style={{minWidth: 0}} // Allow text to shrink
                                            >
                                              <span
                                                className='text-truncate'
                                                title={vessel.fleet_name}
                                                style={{
                                                  display: 'inline-block',
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  whiteSpace: 'nowrap',
                                                  maxWidth: '100%', // or a fixed value like '200px'
                                                }}
                                              >
                                                <VesselLandingLink
                                                  vesselId={vessel.id}
                                                  label={vessel.fleet_name}
                                                  className='text-dark text-decoration-underline'
                                                />
                                              </span>
                                            </button>
                                          </h2>
                                          <div
                                            id={`collapse-orphan-${vessel.id}`}
                                            className={`accordion-collapse collapse ${
                                              isOrphanOpen ? 'show' : ''
                                            }`}
                                            aria-labelledby={`heading-orphan-${vessel.id}`}
                                            data-bs-parent={`#collapse-admin-${admin.id}`}
                                          >
                                            <div className='accordion-body'>
                                              {renderVesselVoyageList(vessel.id, vesselVoyages)}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )
                                  })}

                                  {/* 2) Per-company accordions */}
                                  <div className='accordion' id={`companyAccordion-${admin.id}`}>
                                    {adminCompanies.map((company) => {
                                      const isCompanyOpen = expandedCompanyId === company.id
                                      const companyVessels = vessels.filter(
                                        (v) => v.companyAdmin?.id === company.id && v.active
                                      )

                                      return (
                                        <div key={company.id} className='accordion-item mb-3'>
                                          <h2
                                            className='accordion-header'
                                            id={`heading-company-${company.id}`}
                                          >
                                            <button
                                              className={`accordion-button ${
                                                isCompanyOpen ? '' : 'collapsed'
                                              }`}
                                              type='button'
                                              data-bs-toggle='collapse'
                                              data-bs-target={`#collapse-company-${company.id}`}
                                              aria-expanded={isCompanyOpen}
                                              aria-controls={`collapse-company-${company.id}`}
                                              onClick={() =>
                                                setExpandedCompanyId(
                                                  isCompanyOpen ? null : company.id
                                                )
                                              }
                                            >
                                              {company.name}
                                            </button>
                                          </h2>
                                          <div
                                            id={`collapse-company-${company.id}`}
                                            className={`accordion-collapse collapse ${
                                              isCompanyOpen ? 'show' : ''
                                            }`}
                                            aria-labelledby={`heading-company-${company.id}`}
                                            data-bs-parent={`#companyAccordion-${admin.id}`}
                                          >
                                            <div className='accordion-body py-3'>
                                              <div
                                                className='accordion'
                                                id={`vesselAccordion-${company.id}`}
                                              >
                                                {companyVessels.map((vessel) => {
                                                  const isVesselOpen = expandedVesselId === vessel.id
                                                  const vesselVoyages = voyages.filter(
                                                    (vo) => vo.vessel?.id === vessel.id
                                                  )

                                                  return (
                                                    <div
                                                      key={vessel.id}
                                                      className='accordion-item mb-2'
                                                    >
                                                      <h2
                                                        className='accordion-header'
                                                        id={`heading-vessel-${vessel.id}`}
                                                      >
                                                        <button
                                                          className={`accordion-button ${
                                                            isVesselOpen ? '' : 'collapsed'
                                                          }`}
                                                          type='button'
                                                          data-bs-toggle='collapse'
                                                          data-bs-target={`#collapse-vessel-${vessel.id}`}
                                                          aria-expanded={isVesselOpen}
                                                          aria-controls={`collapse-vessel-${vessel.id}`}
                                                          onClick={() =>
                                                            setExpandedVesselId(
                                                              isVesselOpen ? null : vessel.id
                                                            )
                                                          }
                                                        >
                                                          <VesselLandingLink
                                                            vesselId={vessel.id}
                                                            label={vessel.fleet_name}
                                                            className='text-dark text-decoration-underline'
                                                          />
                                                        </button>
                                                      </h2>
                                                      <div
                                                        id={`collapse-vessel-${vessel.id}`}
                                                        className={`accordion-collapse collapse ${
                                                          isVesselOpen ? 'show' : ''
                                                        }`}
                                                        aria-labelledby={`heading-vessel-${vessel.id}`}
                                                        data-bs-parent={`#vesselAccordion-${company.id}`}
                                                      >
                                                        <div className='accordion-body'>
                                                          {renderVesselVoyageList(vessel.id, vesselVoyages)}
                                                        </div>
                                                      </div>
                                                    </div>
                                                  )
                                                })}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      )
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                    </div>
                  )}
                {/* Company */}
                {scopeUx.actsAsCompanyGroupAdmin &&
                  !selectedVessel && (
                    <>
                      {/* 1) Orphan vessels */}
                      <div className='accordion' id='accordion-orphans'>
                        {vessels
                          .filter((v) => {
                            const targetId =
                              scopeUx.isCompanyGroupAdmin
                                ? Number(roleEntityId)
                                : scopeUx.isCompanyGroupTiedOperator
                                ? Number(companyGroupAdminId)
                                : null

                            const isMatch =
                              v.companyGroupAdmin?.id === targetId &&
                              v.active &&
                              !v.companyAdmin?.id
                            if (!isMatch) return false
                            if (globalState.company?.type === 'company_admin') return false
                            return true
                          })
                          .map((vessel) => {
                            const isOrphanOpen = expandedVesselId === vessel.id
                            const vesselVoyages = voyages.filter(
                              (vo) => vo.vessel?.id === vessel.id
                            )

                            return (
                              <div key={vessel.id} className='accordion-item'>
                                <h2 className='accordion-header' id={`heading-orphan-${vessel.id}`}>
                                  <button
                                    className={`accordion-button ${
                                      isOrphanOpen ? '' : 'collapsed'
                                    }`}
                                    type='button'
                                    data-bs-toggle='collapse'
                                    data-bs-target={`#collapse-orphan-${vessel.id}`}
                                    aria-expanded={isOrphanOpen}
                                    aria-controls={`collapse-orphan-${vessel.id}`}
                                  >
                                     <VesselLandingLink
                                       vesselId={vessel.id}
                                       label={vessel.fleet_name}
                                       className='text-dark text-decoration-underline'
                                     />
                                  </button>
                                </h2>
                                <div
                                  id={`collapse-orphan-${vessel.id}`}
                                  className={`accordion-collapse collapse ${
                                    isOrphanOpen ? 'show' : ''
                                  }`}
                                  aria-labelledby={`heading-orphan-${vessel.id}`}
                                  data-bs-parent='#accordion-orphans'
                                >
                                  <div className='accordion-body'>
                                    {renderVesselVoyageList(vessel.id, vesselVoyages)}
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                      </div>

                      {/* 2) Company‐grouped vessels */}
                      <div className='accordion' id='companyAccordionRole5'>
                        {companies
                          .filter(
                            (c) =>
                              c.cga?.id ===
                                (scopeUx.isCompanyGroupAdmin
                                  ? Number(roleEntityId)
                                  : scopeUx.isCompanyGroupTiedOperator
                                  ? Number(companyGroupAdminId)
                                  : null) && c.active
                          )
                          .filter((c) => {
                            if (!globalState.company) return true
                            if (globalState.company.type === 'company_admin') {
                              return c.id === globalState.company.id
                            }
                            return true
                          })
                          .map((company) => {
                            const isCompanyOpen = expandedCompanyId === company.id
                            const companyVessels = vessels.filter(
                              (v) => v.companyAdmin?.id === company.id && v.active
                            )

                            return (
                              <div key={company.id} className='accordion-item'>
                                <h2
                                  className='accordion-header'
                                  id={`heading-company-${company.id}`}
                                >
                                  <button
                                    className={`accordion-button ${
                                      isCompanyOpen ? '' : 'collapsed'
                                    }`}
                                    type='button'
                                    data-bs-toggle='collapse'
                                    data-bs-target={`#collapse-companyRole5-${company.id}`}
                                    aria-expanded={isCompanyOpen}
                                    aria-controls={`collapse-companyRole5-${company.id}`}
                                  >
                                    {company.name}
                                  </button>
                                </h2>
                                <div
                                  id={`collapse-companyRole5-${company.id}`}
                                  className={`accordion-collapse collapse ${
                                    isCompanyOpen ? 'show' : ''
                                  }`}
                                  aria-labelledby={`heading-company-${company.id}`}
                                  data-bs-parent='#companyAccordionRole5'
                                >
                                  <div className='accordion-body py-3'>
                                    <div className='accordion' id={`vesselAccordion-${company.id}`}>
                                      {companyVessels.map((vessel) => {
                                        const isVesselOpen = expandedVesselId === vessel.id
                                        const vesselVoyages = voyages.filter(
                                          (vo) => vo.vessel?.id === vessel.id
                                        )

                                        return (
                                          <div key={vessel.id} className='accordion-item'>
                                            <h2
                                              className='accordion-header'
                                              id={`heading-vessel-${vessel.id}`}
                                            >
                                              <button
                                                className={`accordion-button ${
                                                  isVesselOpen ? '' : 'collapsed'
                                                }`}
                                                type='button'
                                                data-bs-toggle='collapse'
                                                data-bs-target={`#collapse-vessel-${vessel.id}`}
                                                aria-expanded={isVesselOpen}
                                                aria-controls={`collapse-vessel-${vessel.id}`}
                                              >
                                                 <VesselLandingLink
                                                   vesselId={vessel.id}
                                                   label={vessel.fleet_name}
                                                   className='text-dark text-decoration-underline'
                                                 />
                                              </button>
                                            </h2>
                                            <div
                                              id={`collapse-vessel-${vessel.id}`}
                                              className={`accordion-collapse collapse ${
                                                isVesselOpen ? 'show' : ''
                                              }`}
                                              aria-labelledby={`heading-vessel-${vessel.id}`}
                                              data-bs-parent={`#vesselAccordion-${company.id}`}
                                            >
                                              <div className='accordion-body'>
                                                {renderVesselVoyageList(vessel.id, vesselVoyages)}
                                              </div>
                                            </div>
                                          </div>
                                        )
                                      })}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                      </div>
                    </>
                  )}
                {/* Sub Company */}
                {scopeUx.isCompanyAdmin && !selectedVessel && (
                  <div className='accordion' id='vesselAccordionRole2'>
                    {vessels
                      .filter((vessel) => vessel.companyAdmin?.id === roleEntityId && vessel.active)
                      .filter((vessel) => {
                        if (!globalState.company) return true
                        if (globalState.company.type === 'company_admin') {
                          return vessel.companyAdmin?.id === globalState.company.id
                        }
                        return true
                      })
                      .map((vessel) => {
                        const vesselVoyages = voyages.filter((v) => v.vessel?.id === vessel.id)
                        const isOpen = expandedVesselId === vessel.id

                        return (
                          <div key={vessel.id} className='accordion-item'>
                            <h2 className='accordion-header' id={`heading-vessel-${vessel.id}`}>
                              <button
                                className={`accordion-button ${isOpen ? '' : 'collapsed'}`}
                                type='button'
                                data-bs-toggle='collapse'
                                data-bs-target={`#collapse-vessel-${vessel.id}`}
                                aria-expanded={isOpen}
                                aria-controls={`collapse-vessel-${vessel.id}`}
                              >
                                 <VesselLandingLink
                                   vesselId={vessel.id}
                                   label={vessel.fleet_name}
                                   className='text-dark text-decoration-underline'
                                 />
                              </button>
                            </h2>

                            <div
                              id={`collapse-vessel-${vessel.id}`}
                              className={`accordion-collapse collapse ${isOpen ? 'show' : ''}`}
                              aria-labelledby={`heading-vessel-${vessel.id}`}
                              data-bs-parent='#vesselAccordionRole2'
                            >
                              <div className='accordion-body'>
                                {renderVesselVoyageList(vessel.id, vesselVoyages)}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                  </div>
                )}
              </div>
              <div
                id='pdf-preview-wrapper'
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  zIndex: -1,
                  opacity: 0,
                  pointerEvents: 'none',
                }}
              >
                <div id='pdf-preview' />
              </div>
              <div
                style={{
                  flex: 5,
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                  minHeight: 0,
                  overflow: 'hidden',
                  backgroundColor: '#FFFFFF',
                }}
              >
                {!embedded && (
                <div className='px-5 py-4 border-bottom bg-white'>
                  {/* Top Bar */}
                  <div className='d-flex justify-content-between align-items-center mb-3'>
                    <div>
                      <h3 className='card-title fw-bolder text-gray-900 m-0'>Operations</h3>
                      <span className='text-muted fs-7 fw-semibold'>Overview & Position Reports</span>
                    </div>
                    <div className='d-flex align-items-center gap-3'>
                      {canDownloadAnyReport && (
                        <button
                          className='btn btn-sm btn-light-primary d-flex align-items-center gap-1'
                          onClick={handleExcelDownload}
                          title='Export to Excel'
                        >
                          <KTSVG path='/media/icons/duotune/general/download.svg' className='svg-icon-2' />
                          <span>Export</span>
                        </button>
                      )}
                      {canCreateAnyReport && (
                        <button className='btn btn-sm btn-primary' onClick={() => void openCreateModal()}>
                          <i className='bi bi-plus-lg me-1'></i> Create report
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Context Cards Strip */}
                  <div className='d-flex flex-wrap gap-3 align-items-stretch'>
                    {/* Vessel Info */}
                    <div className='p-3 rounded bg-light border d-flex flex-column justify-content-center' style={{minWidth: '220px'}}>
                      <span className='text-muted fs-8 text-uppercase fw-bold'>Vessel</span>
                      <div className='d-flex align-items-center gap-2 mt-1'>
                        <span className='fw-bold text-gray-900 fs-6'>
                          {selectedVessel?.id ? (
                            <VesselLandingLink
                              vesselId={selectedVessel.id}
                              label={vesselName || selectedVessel.fleet_name}
                              className='text-primary text-hover-primary'
                            />
                          ) : (
                            vesselName || '--'
                          )}
                        </span>
                        {vesselType && (
                          <span className='badge badge-light-secondary fs-8'>{vesselType}</span>
                        )}
                      </div>
                      {imoNumber && <span className='text-gray-500 fs-8 mt-1'>IMO: {imoNumber}</span>}
                    </div>

                    {/* Voyage Info */}
                    {selectedVoyage ? (
                      <div className='p-3 rounded bg-light border d-flex flex-column justify-content-center' style={{minWidth: '240px'}}>
                        <div className='d-flex justify-content-between align-items-center'>
                          <span className='text-muted fs-8 text-uppercase fw-bold'>Voyage</span>
                          {selectedVoyage.active && (
                            <span className='badge badge-light-success fs-8'>Active</span>
                          )}
                        </div>
                        <div className='fw-bold text-gray-900 fs-6 mt-1'>
                          {selectedVoyage.voyageNumber}: {selectedVoyage.departurePort} → {selectedVoyage.arrivalPort || 'TBD'}
                        </div>
                        {selectedVoyage.startDate && (
                          <span className='text-gray-500 fs-8 mt-1'>
                            {new Date(selectedVoyage.startDate).toLocaleDateString('en-GB')}
                            {selectedVoyage.endDate ? ` – ${new Date(selectedVoyage.endDate).toLocaleDateString('en-GB')}` : ''}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className='p-3 rounded bg-light border d-flex flex-column justify-content-center' style={{minWidth: '240px'}}>
                        <span className='text-muted fs-8 text-uppercase fw-bold'>Voyage</span>
                        <span className='text-muted fs-7 mt-1'>No voyage selected</span>
                      </div>
                    )}

                    {/* Metrics Summary */}
                    <div className='p-3 rounded bg-light border d-flex align-items-center gap-3 ms-auto'>
                      <div className='d-flex flex-column align-items-center px-2'>
                        <span className='fs-5 fw-bolder text-gray-900'>{filteredReports.length}</span>
                        <span className='text-muted fs-8'>Reports</span>
                      </div>
                      <div className='border-end h-75'></div>
                      <div className='d-flex flex-column align-items-center px-2'>
                        <span className='fs-5 fw-bolder text-success'>{hasWarningsFalse}</span>
                        <span className='text-muted fs-8'>Complete</span>
                      </div>
                      <div className='border-end h-75'></div>
                      <div className='d-flex flex-column align-items-center px-2'>
                        <span className='fs-5 fw-bolder text-warning'>{hasWarningsTrue}</span>
                        <span className='text-muted fs-8'>Warnings</span>
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {/* Table & Filters Section */}
                <div
                  className='pt-4 px-5'
                  style={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                    paddingBottom: '24px',
                  }}
                >
                  {/* Leg Segmented Pill Filters (if legs exist on the selected voyage) */}
                  {selectedVoyage?.legs && selectedVoyage.legs.length > 0 && (
                    <div className='d-flex align-items-center gap-2 mb-3 flex-wrap py-2 px-3 bg-light rounded border'>
                      <span className='fw-bold text-gray-700 fs-7 me-1'>Legs:</span>
                      <button
                        type='button'
                        className={`btn btn-sm py-1 px-3 ${selectedLegId === null ? 'btn-primary shadow-sm' : 'btn-light'}`}
                        onClick={() => {
                          setSelectedLegId(null)
                          setCurrentPage(1)
                        }}
                      >
                        All Legs ({reports.length})
                      </button>
                      {selectedVoyage.legs.map((leg) => {
                        const legReportCount = reports.filter(
                          (r) => Number(r.legId ?? r.legDto?.id ?? r.leg?.id) === Number(leg.id)
                        ).length
                        const isSelected = selectedLegId === leg.id
                        return (
                          <button
                            key={leg.id}
                            type='button'
                            className={`btn btn-sm py-1 px-3 ${isSelected ? 'btn-primary shadow-sm' : 'btn-light'} d-flex align-items-center gap-1`}
                            onClick={() => {
                              setSelectedLegId(isSelected ? null : leg.id)
                              setCurrentPage(1)
                            }}
                          >
                            <span>L{leg.sequenceNumber}: {leg.departurePort} → {leg.arrivalPort || 'TBD'}</span>
                            {leg.active && (
                              <span className={`badge ${isSelected ? 'bg-white text-primary' : 'badge-light-success text-success'} fs-9 ms-1`}>
                                Active
                              </span>
                            )}
                            <span className={`badge ${isSelected ? 'bg-white text-primary' : 'badge-light-dark'} ms-1`}>
                              {legReportCount}
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  )}

                  {/* Reports Filter Controls */}
                  <div className='d-flex align-items-center gap-2 mb-3 flex-wrap'>
                    <input
                      type='text'
                      className='form-control form-control-sm'
                      style={{width: '220px'}}
                      placeholder='Filter by report name'
                      value={reportNameFilter}
                      onChange={(e) => {
                        setReportNameFilter(e.target.value)
                        setCurrentPage(1)
                      }}
                    />
                    <input
                      type='date'
                      className='form-control form-control-sm'
                      style={{width: '180px'}}
                      value={reportDateFilter}
                      onChange={(e) => {
                        setReportDateFilter(e.target.value)
                        setCurrentPage(1)
                      }}
                    />
                    <input
                      type='time'
                      className='form-control form-control-sm'
                      style={{width: '140px'}}
                      value={reportTimeFilter}
                      onChange={(e) => {
                        setReportTimeFilter(e.target.value)
                        setCurrentPage(1)
                      }}
                    />
                    {(reportNameFilter || reportDateFilter || reportTimeFilter || selectedLegId !== null) && (
                      <button className='btn btn-sm btn-light' onClick={clearReportFilters}>
                        Clear filters
                      </button>
                    )}
                  </div>

                  <div
                    className='report-table'
                    style={{
                      flex: 1,
                      minHeight: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      borderRadius: 0,
                      boxShadow: 'none',
                    }}
                  >
                    <div
                      className='table-responsive'
                      style={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        borderRadius: 0,
                      }}
                    >
                    <table className='table table-bordered align-middle'>
                      <thead className='table-header text-start'>
                        <tr>
                          <th
                            onClick={() => handleSort('reportType')}
                            className='cursor-pointer'
                            style={{paddingLeft: '2rem'}}
                          >
                            REPORT NAME
                            <KTSVG
                              path={`/media/map/sort-col-${
                                sortConfig.key === 'reportType'
                                  ? sortConfig.direction === 'asc'
                                    ? 'up-black'
                                    : 'down-black'
                                  : 'grey'
                              }.svg`}
                              className='svg-icon ms-2 custom-sort-icon'
                            />
                          </th>

                          <th onClick={() => handleSort('voyageNumber')} className='cursor-pointer'>
                            VOYAGE / LEG
                            <KTSVG
                              path={`/media/map/sort-col-${
                                sortConfig.key === 'voyageNumber'
                                  ? sortConfig.direction === 'asc'
                                    ? 'up-black'
                                    : 'down-black'
                                  : 'grey'
                              }.svg`}
                              className='svg-icon ms-2 custom-sort-icon'
                            />
                          </th>

                          <th
                            onClick={() => handleSort('reportDateTime')}
                            className='cursor-pointer'
                          >
                            REPORT DATE & TIME
                            <KTSVG
                              path={`/media/map/sort-col-${
                                sortConfig.key === 'reportDateTime'
                                  ? sortConfig.direction === 'asc'
                                    ? 'up-black'
                                    : 'down-black'
                                  : 'grey'
                              }.svg`}
                              className='svg-icon ms-2 custom-sort-icon'
                            />
                          </th>

                          <th onClick={() => handleSort('createdBy')} className='cursor-pointer'>
                            CREATED BY
                            <KTSVG
                              path={`/media/map/sort-col-${
                                sortConfig.key === 'createdBy'
                                  ? sortConfig.direction === 'asc'
                                    ? 'up-black'
                                    : 'down-black'
                                  : 'grey'
                              }.svg`}
                              className='svg-icon ms-2 custom-sort-icon'
                            />
                          </th>

                          <th onClick={() => handleSort('status')} className='cursor-pointer'>
                            STATUS
                            <KTSVG
                              path={`/media/map/sort-col-${
                                sortConfig.key === 'status'
                                  ? sortConfig.direction === 'asc'
                                    ? 'up-black'
                                    : 'down-black'
                                  : 'grey'
                              }.svg`}
                              className='svg-icon ms-2 custom-sort-icon'
                            />
                          </th>

                          <th>ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody className='table-body text-start'>
                        {loadingReports ? (
                          <TableSkeleton rows={8} cols={6} />
                        ) : !selectedVoyage && !searchTerm.trim() ? (
                          <tr>
                            <td colSpan={6} className='text-center text-muted py-5'>
                              <div className='d-flex flex-column align-items-center justify-content-center py-4'>
                                <KTSVG path='/media/icons/duotune/general/gen004.svg' className='svg-icon-3x text-muted mb-2' />
                                <span className='fw-bold fs-6 text-gray-700'>No Voyage Selected</span>
                                <span className='text-muted fs-7 mt-1'>Please select a voyage from the sidebar to view its position reports.</span>
                              </div>
                            </td>
                          </tr>
                        ) : currentReports.length === 0 ? (
                          <tr>
                            <td colSpan={6} className='text-center text-muted py-5'>
                              {reports.length === 0
                                ? 'No reports created for the selected voyage.'
                                : 'No matching reports found.'}
                            </td>
                          </tr>
                        ) : (
                          currentReports.map((row, idx) => {
                            const rowStatus = normalizeReportStatus(row.status)
                            const rowKey = row?.id ?? row?.reportId ?? row?.assignment?.id ?? idx
                            return (
                            <tr key={rowKey}>
                              <td>
                                <div className='d-flex align-items-center'>
                                  {row.hasWarnings ? (
                                    <KTSVG path='/media/map/Incomplete.svg' className='svg-icon-2' />
                                  ) : (
                                    <KTSVG path='/media/map/complete.svg' className='svg-icon-2' />
                                  )}
                                  <span className='ps-2 fw-semibold text-gray-800'>{row.reportType}</span>
                                </div>
                              </td>
                              <td>
                                <div className='d-flex flex-column'>
                                  <span className='fw-bold text-gray-800'>{row.voyageNumber || '--'}</span>
                                  {(row.legDto || row.legSequenceNumber != null || row.legId) && (
                                    <span className='badge badge-light-info text-info fw-semibold mt-1 align-self-start' style={{fontSize: '11px'}}>
                                      {row.legDto ? (
                                        `L${row.legDto.sequenceNumber}: ${row.legDto.departurePort || ''} → ${row.legDto.arrivalPort || 'TBD'}`
                                      ) : row.legSequenceNumber ? (
                                        `Leg ${row.legSequenceNumber}`
                                      ) : (
                                        `Leg #${row.legId}`
                                      )}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td>
                                {row.reportDateTime
                                  ? `${new Date(row.reportDateTime)
                                      .toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric',
                                      })
                                      .replace(/ /g, '-')}`
                                  : '--'}{' '}
                                {row.reportDateTime
                                  ? new Date(row.reportDateTime).toLocaleTimeString('en-GB')
                                  : ''}
                              </td>

                              <td>{row?.createdBy?.rank}</td>
                              <td>
                                {rowStatus === 'SUBMITTED' ? (
                                  <span className='badge submitted d-inline-flex align-items-center gap-1'>
                                    <i className='bi bi-lock-fill me-1'></i> Submitted &amp; Locked
                                  </span>
                                ) : (
                                  <span className='badge draft'>Draft</span>
                                )}
                              </td>
                              <td>
                                {/* <button
                                                                    onClick={() => {
                                                                        if (row.assignment?.id) {
                                                                            navigate(`/operations/create/${row.assignment.id}`, {
                                                                                state: {
                                                                                    draftReport: row,
                                                                                    isEditMode: true,
                                                                                    selectedVoyage,
                                                                                },
                                                                            });
                                                                        } else {
                                                                            console.error("No assignment ID found");
                                                                        }
                                                                    }}
                                                                    className="btn btn-sm px-0"
                                                                >
                                                                    <KTSVG path="/media/map/edit-active.svg" />
                                                                </button> */}
                                {rowStatus === 'SUBMITTED' && (
                                  <>
                                    {canUpdateReport(row) && (
                                        <button
                                          onClick={() => {
                                            if (row.assignment?.id) {
                                              navigate(`/operations/create/${row.assignment.id}`, {
                                                state: {
                                                  draftReport: row,
                                                  isEditMode: true,
                                                  selectedVoyage,
                                                  selectedReportName: row.reportType,
                                                },
                                              })
                                            } else {
                                              console.error('No assignment ID found')
                                            }
                                          }}
                                          className='btn btn-sm px-0'
                                          title='Edit submitted report'
                                        >
                                          <KTSVG path='/media/map/edit-active.svg' />
                                        </button>
                                      )}

                                    {canReadReport(row) && (
                                    <button
                                      className='btn btn-sm px-0'
                                      onClick={() => handlePreviewReportView(row)}
                                    >
                                      <KTSVG path='/media/map/ph_eye.svg' />
                                    </button>
                                    )}
                                    {canDownloadReport(row) && (
                                    <button
                                      className='btn btn-sm px-0'
                                      onClick={() => handleDownload(row)}
                                    >
                                      <KTSVG
                                        path='/media/map/DownloadSimple.svg'
                                        className='svg-icon-2'
                                      />
                                    </button>
                                    )}
                                    {canDeleteReport(row) && (
                                    <button
                                      onClick={() => {
                                        setIsDeleteCreatedReportModalOpen(true)
                                        setDraftReportId(row.id)
                                      }}
                                      className='btn btn-sm px-0 text-hover-danger'
                                      title='Delete report'
                                    >
                                      <KTSVG path='/media/map/trash.svg' />
                                    </button>
                                    )}
                                  </>
                                )}

                                {rowStatus === 'DRAFT' && (
                                  <>
                                    {canUpdateReport(row) && (
                                    <button
                                      onClick={() => {
                                        if (row.assignment?.id) {
                                          navigate(`/operations/create/${row.assignment.id}`, {
                                            state: {
                                              draftReport: row,
                                              isEditMode: true,
                                              selectedVoyage,
                                              selectedReportName: row.reportType,
                                            }
                                          })
                                        } else {
                                          console.error('No assignment ID found')
                                        }
                                      }}
                                      className='btn btn-sm px-0'
                                    >
                                      <KTSVG path='/media/map/edit-active.svg' />
                                    </button>
                                    )}
                                    {canDeleteReport(row) && (
                                    <button
                                      onClick={() => {
                                        setIsDeleteCreatedReportModalOpen(true)
                                        setDraftReportId(row.id)
                                      }}
                                      className='btn btn-sm px-0'
                                    >
                                      <KTSVG path='/media/map/trash.svg' />
                                    </button>
                                    )}
                                  </>
                                )}
                              </td>
                            </tr>
                            )
                          })
                        )}
                      </tbody>
                    </table>
                    </div>
                    {/* Pagination */}
                    <div
                      className='pagination-wrapper d-flex justify-content-between align-items-center py-3'
                      style={{flexShrink: 0, backgroundColor: '#ffffff', borderRadius: 0}}
                    >
                      <div className='d-flex align-items-center'>
                        <span className='text-muted me-2'>Rows per page</span>
                        <SearchableSelect
                          className='form-select'
                          style={{
                            borderRadius: '20px',
                            width: '70px',
                            border: '1px solid #dee2e6',
                            fontSize: '14px',
                            padding: '4px 8px',
                          }}
                          value={rowsPerPage}
                          onChange={handleRowsPerPageChange}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                        </SearchableSelect>
                      </div>

                      <div className='d-flex align-items-center'>
                        <span className='text-muted me-3' style={{fontSize: '14px'}}>
                          Showing{' '}
                          <strong>
                            {filteredReports.length === 0
                              ? 0
                              : (currentPage - 1) * rowsPerPage + 1}
                            -{Math.min(currentPage * rowsPerPage, filteredReports.length)}
                          </strong>{' '}
                          of <strong>{filteredReports.length}</strong>
                        </span>

                        <nav>
                          <ul className='pagination pagination-sm mb-0' style={{gap: '2px'}}>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className='page-link text-muted'
                                style={{
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #dee2e6',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  borderRadius: '6px',
                                }}
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                              >
                                ‹
                              </button>
                            </li>

                            {(() => {
                              const pages = []
                              const showPages = 5 // Show 5 page numbers at most
                              let startPage = Math.max(1, currentPage - 2)
                              let endPage = Math.min(totalPages, startPage + showPages - 1)

                              // Adjust start if we're near the end
                              if (endPage - startPage < showPages - 1) {
                                startPage = Math.max(1, endPage - showPages + 1)
                              }

                              // Add first page and ellipsis if needed
                              if (startPage > 1) {
                                pages.push(
                                  <li key={1} className='page-item'>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        minWidth: '40px',
                                        borderRadius: '6px',
                                      }}
                                      onClick={() => handlePageChange(1)}
                                    >
                                      1
                                    </button>
                                  </li>
                                )

                                if (startPage > 2) {
                                  pages.push(
                                    <li key='ellipsis1' className='page-item disabled'>
                                      <span
                                        className='page-link border-0 text-muted'
                                        style={{backgroundColor: 'transparent', padding: '4px 8px'}}
                                      >
                                        ...
                                      </span>
                                    </li>
                                  )
                                }
                              }

                              // Add page numbers
                              for (let i = startPage; i <= endPage; i++) {
                                pages.push(
                                  <li
                                    key={i}
                                    className={`page-item ${currentPage === i ? 'active' : ''}`}
                                  >
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor:
                                          currentPage === i ? '#F4F9FF' : 'transparent',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        minWidth: '40px',
                                        borderRadius: '6px',
                                        outline: 'none',
                                        boxShadow: 'none',
                                      }}
                                      onClick={() => handlePageChange(i)}
                                    >
                                      {i}
                                    </button>
                                  </li>
                                )
                              }

                              // Add ellipsis and last page if needed
                              if (endPage < totalPages) {
                                if (endPage < totalPages - 1) {
                                  pages.push(
                                    <li key='ellipsis2' className='page-item disabled'>
                                      <span
                                        className='page-link border-0 text-muted'
                                        style={{backgroundColor: 'transparent', padding: '4px 8px'}}
                                      >
                                        ...
                                      </span>
                                    </li>
                                  )
                                }

                                pages.push(
                                  <li key={totalPages} className='page-item'>
                                    <button
                                      className='page-link text-muted'
                                      style={{
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        minWidth: '40px',
                                        borderRadius: '6px',
                                      }}
                                      onClick={() => handlePageChange(totalPages)}
                                    >
                                      {totalPages}
                                    </button>
                                  </li>
                                )
                              }

                              return pages
                            })()}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? 'disabled' : ''
                              }`}
                            >
                              <button
                                className='page-link text-muted'
                                style={{
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #dee2e6',
                                  padding: '8px 12px',
                                  fontSize: '14px',
                                  borderRadius: '6px',
                                }}
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                              >
                                ›
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {isCreateModalOpen && (
          <CreateReportModal
            isOpen={isCreateModalOpen}
            onClose={closeModal}
            selectedVoyage={selectedVoyage}
          />
        )}
        {isAddVoyageModalOpen && (
          <AddVoyageModal
            isOpen={isAddVoyageModalOpen}
            onClose={() => {
              setIsAddVoyageModalOpen(false)
            }}
            onVoyageAdded={fetchVoyages}
          />
        )}
        {isDeleteCreatedReportModalOpen && (
          <DeleteCreatedReportModal
            isOpen={isDeleteCreatedReportModalOpen}
            onClose={() => setIsDeleteCreatedReportModalOpen(false)}
            reportId={draftReportId}
            onReportDeleted={fetchCreatedReportsList}
          />
        )}
        <ToastContainer
          position='top-center'
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          style={{top: '5rem'}}
        />
      </div>
    </div>
  )
}

export {OperationsList}
